import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, Loader2, ShieldAlert, MapPin, FileText, Check, CheckCheck, Mail, Users2, MessageCircle, Send, Star, UserCog, Power, Plus, KeyRound, Download, Megaphone, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { useSettings } from "../lib/settings";
import { createAdmin, setAdminActive, regenerateAdminPassword } from "../lib/adminActions";
import GeneratedCredential from "../components/GeneratedCredential";
import { requestNotificationPermission, getPushToken, getPlatform } from "../lib/native";

const SOS_ALERT_PUSH_KEY = "ts_sos_alert_push_enabled";

function NewTravelerCredentials({ traveler }) {
  const { settings } = useSettings();
  const user = auth.current();
  const slug = user?.agency_slug || "default";
  return (
    <GeneratedCredential
      testId="new-traveler-credentials"
      heading={`Acceso creado para ${traveler.traveler_name}`}
      loginUrl={`${window.location.origin}/a/${slug}/login`}
      bookingId={traveler.booking_id}
      secondaryLabel="Apellido"
      secondaryValue={traveler.last_name}
      password={traveler.password}
      phone={traveler.phone}
      email={traveler.email}
      greetingName={traveler.traveler_name.split(" ")[0]}
      productName={settings.agency_name}
    />
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const user = auth.current();
  const { settings } = useSettings();
  const [bookingId, setBookingId] = useState("REG-2026-001");
  const [regCode, setRegCode] = useState("");
  const [newTravelerLastName, setNewTravelerLastName] = useState("");
  const [newTravelerName, setNewTravelerName] = useState("");
  const [newTravelerEmail, setNewTravelerEmail] = useState("");
  const [newTravelerPhone, setNewTravelerPhone] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [sosPushEnabled, setSosPushEnabled] = useState(
    () => localStorage.getItem(SOS_ALERT_PUSH_KEY) === "1"
  );
  const [sosPushBusy, setSosPushBusy] = useState(false);
  const [travelerSearch, setTravelerSearch] = useState("");
  const [resettingTravelerId, setResettingTravelerId] = useState("");
  const [resetTravelerCredential, setResetTravelerCredential] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [loadingMoreLeads, setLoadingMoreLeads] = useState(false);
  // El límite vive en un ref, no en state: el sondeo de 8s se arma UNA vez
  // en el useEffect de montaje (deps []) y closurea loadLeads de ese
  // primer render — si el límite viviera en state, el poll se quedaría
  // viendo el valor original para siempre después de un "cargar más" (el
  // mismo bug de closure obsoleto que se corrigió en SOSPage.jsx). Un ref
  // se lee al momento, no al crear el closure.
  const leadsLimitRef = useRef(50);
  const [bookings, setBookings] = useState([]);
  const [chatBookingId, setChatBookingId] = useState("");
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatText, setChatText] = useState("");
  const chatScrollRef = useRef(null);
  const chatCardRef = useRef(null);
  const replyToFeedback = (bId) => {
    setChatBookingId(bId);
    chatCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [loadingMoreFeedback, setLoadingMoreFeedback] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ booking_id: "", last_name: "", traveler_name: "", email: "" });
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [adminError, setAdminError] = useState("");
  const [newAdminCredential, setNewAdminCredential] = useState(null);
  const [resettingAdminId, setResettingAdminId] = useState("");
  const [unreadIds, setUnreadIds] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [newPromo, setNewPromo] = useState({ title: "", subtitle: "", destination: "", price_from: "", currency: "USD", image_url: "", tag: "EXCLUSIVO", discount: "" });
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [sendingPromoId, setSendingPromoId] = useState("");
  const [promoSentMsg, setPromoSentMsg] = useState("");

  useEffect(() => {
    if (!user || !["master", "agency_admin"].includes(user.role)) { navigate("/admin/login"); return; }
    loadAlerts();
    loadLeads();
    loadBookings();
    loadFeedback();
    loadAdmins();
    loadUnreadChats();
    loadPromotions();
    // SOS y chats sin leer sí necesitan verse casi al instante — se quedan
    // en 8s. Los leads (formularios de "me interesa" en promociones) no
    // tienen nada de urgente: nadie espera una respuesta en segundos por
    // pedir información de un viaje. Sondearlos igual de seguido solo
    // gastaba consultas de más en el clúster gratuito sin ganar nada real.
    const fast = setInterval(() => { loadAlerts(); loadUnreadChats(); }, 8000);
    const leadsInterval = setInterval(loadLeads, 5 * 60 * 1000);
    return () => { clearInterval(fast); clearInterval(leadsInterval); };
  }, []);

  useEffect(() => {
    if (!chatBookingId) return;
    loadChat(chatBookingId);
    markChatRead(chatBookingId);
    const i = setInterval(() => loadChat(chatBookingId), 5000);
    return () => clearInterval(i);
  }, [chatBookingId]);

  const loadUnreadChats = async () => {
    try { const { data } = await api.get("/admin/unread-chats"); setUnreadIds(data.unread_booking_ids || []); } catch {}
  };

  const markChatRead = async (bId) => {
    try {
      await api.post(`/admin/chats/${bId}/mark-read`);
      setUnreadIds((ids) => ids.filter((id) => id !== bId));
    } catch {}
  };

  const loadAlerts = async () => {
    try { const { data } = await api.get("/sos/active"); setAlerts(data); } catch {}
  };

  const loadLeads = async () => {
    try {
      const { data } = await api.get("/leads", { params: { skip: 0, limit: leadsLimitRef.current } });
      setLeads(data.items);
      setLeadsTotal(data.total);
    } catch {}
  };

  const loadMoreLeads = async () => {
    setLoadingMoreLeads(true);
    leadsLimitRef.current += 50;
    try {
      const { data } = await api.get("/leads", { params: { skip: 0, limit: leadsLimitRef.current } });
      setLeads(data.items);
      setLeadsTotal(data.total);
    } catch {
      leadsLimitRef.current -= 50; // revertir para que reintentar pida lo mismo, no más
      toast.error("No se pudo cargar más leads. Intenta de nuevo.");
    } finally {
      setLoadingMoreLeads(false);
    }
  };

  const exportLeadsCsv = async () => {
    try {
      const resp = await api.get("/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el CSV. Intenta de nuevo.");
    }
  };

  const loadPromotions = async () => {
    try { const { data } = await api.get("/promotions/mine"); setPromotions(data); } catch {}
  };

  const createPromotion = async (e) => {
    e.preventDefault();
    setPromoError("");
    setCreatingPromo(true);
    try {
      await api.post("/promotions", { ...newPromo, price_from: parseFloat(newPromo.price_from) || 0, discount: parseInt(newPromo.discount, 10) || 0 });
      setNewPromo({ title: "", subtitle: "", destination: "", price_from: "", currency: "USD", image_url: "", tag: "EXCLUSIVO", discount: "" });
      loadPromotions();
    } catch (e) {
      setPromoError(e?.response?.data?.detail || "No se pudo crear la promoción");
    } finally {
      setCreatingPromo(false);
    }
  };

  const deletePromotion = async (id) => {
    try { await api.delete(`/promotions/${id}`); loadPromotions(); } catch {}
  };

  const sendPromotionPush = async (id) => {
    setSendingPromoId(id);
    setPromoSentMsg("");
    setPromoError("");
    try {
      const { data } = await api.post(`/promotions/${id}/send-push`);
      setPromoSentMsg(`Enviado a ${data.sent_to} viajero(s).`);
    } catch (e) {
      setPromoError(e?.response?.data?.detail || "No se pudo enviar el push");
    } finally {
      setSendingPromoId("");
    }
  };

  const loadBookings = async () => {
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
      if (data.length && !chatBookingId) setChatBookingId(data[0].booking_id);
    } catch {}
  };

  const loadFeedback = async () => {
    try {
      const { data } = await api.get("/feedback", { params: { skip: 0, limit: 50 } });
      setFeedbacks(data.items);
      setFeedbackTotal(data.total);
    } catch {}
  };

  const loadMoreFeedback = async () => {
    setLoadingMoreFeedback(true);
    try {
      const { data } = await api.get("/feedback", { params: { skip: feedbacks.length, limit: 50 } });
      setFeedbacks((prev) => [...prev, ...data.items]);
      setFeedbackTotal(data.total);
    } catch {
      toast.error("No se pudo cargar más feedback. Intenta de nuevo.");
    } finally {
      setLoadingMoreFeedback(false);
    }
  };

  const loadChat = async (bId) => {
    try {
      const { data } = await api.get(`/chat/messages/${bId}`);
      setChatMsgs(data);
      setTimeout(() => chatScrollRef.current?.scrollTo({ top: chatScrollRef.current.scrollHeight }), 50);
    } catch {}
  };

  const sendChatReply = async (e) => {
    e.preventDefault();
    if (!chatText.trim() || !chatBookingId) return;
    try {
      const { data } = await api.post("/chat/messages", {
        booking_id: chatBookingId,
        sender: `${settings.agency_name} (Soporte)`,
        type: "text",
        content: chatText,
      });
      setChatMsgs((m) => [...m, data]);
      setChatText("");
      setTimeout(() => chatScrollRef.current?.scrollTo({ top: chatScrollRef.current.scrollHeight }), 50);
    } catch {}
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    const params = new URLSearchParams({
      booking_id: bookingId,
      file_id: regCode,
      last_name: newTravelerLastName,
      traveler_name: newTravelerName,
      email: newTravelerEmail,
      phone: newTravelerPhone,
    });
    try {
      const { data } = await api.post(`/admin/upload-pdf?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data?.new_traveler) {
        setNewTravelerLastName("");
        setNewTravelerName("");
        setNewTravelerEmail("");
        setNewTravelerPhone("");
        loadBookings();
      }
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Error" });
    } finally {
      setUploading(false);
    }
  };

  const resolveAlert = async (id) => {
    try {
      await api.post(`/sos/${id}/resolve`);
      loadAlerts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo resolver la alerta. Intenta de nuevo.");
    }
  };

  const enableSosAlertPush = async () => {
    // Antes, un viajero SIN grupo de emergencia armado disparaba un SOS
    // "silencioso": quedaba guardado, pero nadie se enteraba hasta entrar a
    // mirar el panel. Esto suscribe este dispositivo a un push en cuanto
    // CUALQUIER viajero de la agencia dispare un SOS, tenga o no grupo.
    setSosPushBusy(true);
    try {
      const perm = await requestNotificationPermission();
      if (perm !== "granted") {
        toast.error("Necesitas permitir las notificaciones para activar las alertas de SOS.");
        return;
      }
      const fcmToken = await getPushToken();
      if (!fcmToken) {
        toast.error("No se pudo obtener el token de notificaciones de este navegador.");
        return;
      }
      await api.post(`/agencies/${user.agency_slug || "default"}/register-alert-device`, {
        fcm_token: fcmToken,
        member_name: user.name || "Admin",
        platform: getPlatform(),
      });
      localStorage.setItem(SOS_ALERT_PUSH_KEY, "1");
      setSosPushEnabled(true);
      toast.success("Listo — vas a recibir un push cuando cualquier viajero active el SOS.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo activar las alertas push.");
    } finally {
      setSosPushBusy(false);
    }
  };

  const resetTravelerPassword = async (bookingId) => {
    setResettingTravelerId(bookingId);
    setResetTravelerCredential(null);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/regenerate-password`);
      setResetTravelerCredential(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo regenerar la contraseña.");
    } finally {
      setResettingTravelerId("");
    }
  };

  const filteredBookings = bookings.filter((b) => {
    const q = travelerSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (b.traveler_name || "").toLowerCase().includes(q) ||
      (b.booking_id || "").toLowerCase().includes(q) ||
      (b.file_id || "").toLowerCase().includes(q)
    );
  });

  const loadAdmins = async () => {
    try { const { data } = await api.get("/admin/admins"); setAdmins(data); } catch {}
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    setAdminError("");
    setNewAdminCredential(null);
    setAddingAdmin(true);
    try {
      const data = await createAdmin(newAdmin);
      setNewAdminCredential(data);
      setNewAdmin({ booking_id: "", last_name: "", traveler_name: "", email: "" });
      loadAdmins();
    } catch (e) {
      setAdminError(e?.response?.data?.detail || "No se pudo crear el administrador");
    } finally {
      setAddingAdmin(false);
    }
  };

  const toggleAdmin = async (a) => {
    setAdminError("");
    try {
      await setAdminActive(a.booking_id, !a.active);
      loadAdmins();
    } catch (e) {
      setAdminError(e?.response?.data?.detail || "No se pudo actualizar el administrador");
    }
  };

  // Antes esta pantalla no tenía forma de resetear la contraseña de un
  // admin ya existente (solo existía en el panel de agencias del master) —
  // reusa la misma tarjeta GeneratedCredential de "crear admin" para que
  // la nueva contraseña también salga con los botones de compartir por
  // WhatsApp/correo, no solo como texto plano.
  const resetAdminPwd = async (a) => {
    setAdminError("");
    setNewAdminCredential(null);
    setResettingAdminId(a.booking_id);
    try {
      const data = await regenerateAdminPassword(a.booking_id);
      setNewAdminCredential({
        booking_id: a.booking_id,
        traveler_name: a.traveler_name,
        email: a.email,
        password: data.password,
        isReset: true,
      });
    } catch (e) {
      setAdminError(e?.response?.data?.detail || "No se pudo generar la contraseña");
    } finally {
      setResettingAdminId("");
    }
  };

  if (!user) return null;

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 space-y-12">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Panel Admin</p>
          <h1 className="font-display text-5xl font-light tracking-tight">
            Centro de <span className="text-[var(--brand-cyan,#FF85AC)]">Operaciones</span>
          </h1>
        </div>

        {/* SOS Alerts */}
        <article className="glass rounded-3xl p-8 border border-[#FF3B30]/30">
          <div className="flex items-center gap-3 mb-6">
            <ShieldAlert className="w-5 h-5 text-[#FF3B30]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Alertas SOS Activas</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-[#FF3B30]">
              {alerts.length} activas
            </span>
          </div>
          {!sosPushEnabled && (
            <button
              onClick={enableSosAlertPush}
              disabled={sosPushBusy}
              data-testid="enable-sos-alert-push-btn"
              className="mb-6 w-full text-left text-xs px-4 py-3 rounded-2xl border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 disabled:opacity-50"
            >
              {sosPushBusy
                ? "Activando…"
                : "🔔 Activar alertas push — enterate en el momento si cualquier viajero activa el SOS, tenga o no grupo armado"}
            </button>
          )}
          {alerts.length === 0 ? (
            <p className="text-sm text-neutral-500">Sin alertas activas en este momento.</p>
          ) : (
            <div className="space-y-3" data-testid="active-alerts">
              {alerts.map((a) => (
                <div key={a.id} className="flex items-start justify-between gap-4 p-4 rounded-2xl bg-black/40 border border-[#FF3B30]/40">
                  <div>
                    <p className="font-display text-base">{a.sender_name}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{a.created_at}</p>
                    <a href={a.maps_url} target="_blank" rel="noreferrer" className="text-sm text-[var(--brand-cyan,#FF85AC)] hover:underline inline-flex items-center gap-1 mt-2">
                      <MapPin className="w-3.5 h-3.5" /> {a.latitude.toFixed(4)}, {a.longitude.toFixed(4)}
                    </a>
                  </div>
                  <button
                    onClick={() => resolveAlert(a.id)}
                    data-testid={`resolve-${a.id}`}
                    className="text-xs uppercase tracking-[0.25em] text-[var(--brand-cyan,#FF85AC)] hover:bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/30 rounded-full px-4 py-2"
                  >
                    Resolver
                  </button>
                </div>
              ))}
            </div>
          )}
        </article>

        {/* PDF Parser */}
        <article className="glass rounded-3xl p-8 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Parser de Itinerarios (PDF)</h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mb-5">
            <div className="sm:col-span-1">
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Booking ID</label>
              <input
                value={bookingId}
                onChange={(e) => setBookingId(e.target.value)}
                list="existing-bookings-datalist"
                data-testid="admin-booking-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
              />
              <datalist id="existing-bookings-datalist">
                {bookings.map((b) => (
                  <option key={b.booking_id} value={b.booking_id}>
                    {b.traveler_name}{b.file_id ? ` · ${b.file_id}` : ""}
                  </option>
                ))}
              </datalist>
              <p className="text-xs text-neutral-500 mt-1.5">
                Si es un documento complementario de un viajero que ya existe,
                elige su Booking ID de la lista en vez de escribirlo de memoria.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Archivo PDF</label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="pdf-input"
                className="block w-full text-sm text-neutral-300 file:mr-4 file:py-2.5 file:px-5 file:rounded-full file:border-0 file:bg-[var(--brand-cyan,#FF85AC)] file:text-black file:font-semibold hover:file:bg-[#FF9DC0]"
              />
            </div>
          </div>

          <div className="mb-5">
            <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">
              Número de registro / REG (solo para documentos complementarios)
            </label>
            <input
              value={regCode}
              onChange={(e) => setRegCode(e.target.value)}
              placeholder="Déjalo vacío en el primer PDF de un viajero — se detecta con IA"
              data-testid="admin-reg-input"
              className="w-full bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <p className="text-xs text-neutral-500 mt-1.5">
              En el PDF inicial de un Booking ID, el REG se detecta automáticamente
              del archivo — este campo se ignora. Si ya le subiste un itinerario y
              ahora agregas un documento adicional (pase de abordar, hotel, etc.),
              escribe aquí el REG de ese viajero para confirmarlo.
            </p>
          </div>

          <p className="text-xs text-neutral-500 mb-3">
            Si el Booking ID es nuevo, completa estos datos para crear su acceso al mismo tiempo. Si ya existe, se ignoran.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-5">
            <input
              value={newTravelerName}
              onChange={(e) => setNewTravelerName(e.target.value)}
              placeholder="Nombre del viajero"
              data-testid="new-traveler-name-input"
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <input
              value={newTravelerLastName}
              onChange={(e) => setNewTravelerLastName(e.target.value)}
              placeholder="Apellido"
              data-testid="new-traveler-lastname-input"
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <input
              type="email"
              value={newTravelerEmail}
              onChange={(e) => setNewTravelerEmail(e.target.value)}
              placeholder="Correo (opcional)"
              data-testid="new-traveler-email-input"
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <input
              value={newTravelerPhone}
              onChange={(e) => setNewTravelerPhone(e.target.value)}
              placeholder="Teléfono (opcional, para WhatsApp)"
              data-testid="new-traveler-phone-input"
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
          </div>

          <button
            onClick={upload}
            disabled={!file || uploading}
            data-testid="upload-pdf-btn"
            className="bg-[var(--brand-cyan,#FF85AC)] text-black px-7 py-3 rounded-full font-semibold hover:bg-[#FF9DC0] disabled:opacity-50 inline-flex items-center gap-2"
          >
            {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
            <UploadCloud className="w-4 h-4" />
            Subir y Extraer
          </button>

          {result?.error && <p className="mt-4 text-sm text-[#FF3B30]">Error: {result.error}</p>}
          {result?.ok && (
            <div className="mt-6 p-5 rounded-2xl bg-[var(--brand-cyan,#FF85AC)]/5 border border-[var(--brand-cyan,#FF85AC)]/30" data-testid="parse-result">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)] mb-2">
                <Check className="inline w-3.5 h-3.5 mr-1" />
                {result.count} servicios añadidos
              </p>
              <ul className="space-y-1">
                {result.itineraries?.map((it) => (
                  <li key={it.id} className="text-sm text-neutral-300">
                    <span className="text-[var(--brand-cyan,#FF85AC)]">{it.reg_code}</span> — {it.title} · {it.start_date}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result?.new_traveler && (
            <NewTravelerCredentials traveler={result.new_traveler} />
          )}
        </article>

        {/* Leads */}
        <article className="glass rounded-3xl p-8 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <Users2 className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Leads de Promociones</h2>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[var(--brand-cyan,#FF85AC)]">
              {leads.length} de {leadsTotal} interesados
            </span>
            {leads.length > 0 && (
              <button
                onClick={exportLeadsCsv}
                data-testid="export-leads-csv-btn"
                className="ml-auto inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-[var(--brand-cyan,#FF85AC)]"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
            )}
          </div>
          {leads.length === 0 ? (
            <p className="text-sm text-neutral-500">Aún no hay interesados.</p>
          ) : (
            <div className="overflow-x-auto" data-testid="leads-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-neutral-500 border-b border-white/10">
                    <th className="py-3 pr-4">Origen</th>
                    <th className="py-3 pr-4">Nombre</th>
                    <th className="py-3 pr-4">Contacto</th>
                    <th className="py-3 pr-4">Edad</th>
                    <th className="py-3 pr-4">Género</th>
                    <th className="py-3 pr-4">Ciudad / Estado</th>
                    <th className="py-3 pr-4">Promoción</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-white/5" data-testid={`lead-row-${l.id}`}>
                      <td className="py-3 pr-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] ${
                          l.source === "traveler"
                            ? "bg-[var(--brand-cyan,#FF85AC)]/15 text-[var(--brand-cyan,#FF85AC)] border border-[var(--brand-cyan,#FF85AC)]/30"
                            : "bg-white/5 text-neutral-400 border border-white/10"
                        }`}>
                          {l.source === "traveler" ? "Cliente" : "Invitado"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-white">{l.name}</td>
                      <td className="py-3 pr-4 text-neutral-400">
                        {l.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3 text-[var(--brand-cyan,#FF85AC)]/70" />{l.email}</span>}
                        {!l.email && l.whatsapp && <span>WhatsApp: {l.whatsapp}</span>}
                        {!l.email && !l.whatsapp && l.booking_id && <span className="text-neutral-500">{l.booking_id}</span>}
                      </td>
                      <td className="py-3 pr-4 text-neutral-400">{l.age ?? "—"}</td>
                      <td className="py-3 pr-4 text-neutral-400">{l.gender ? l.gender.replace("_", " ") : "—"}</td>
                      <td className="py-3 pr-4 text-neutral-400">{l.city || l.state ? `${l.city || "—"}${l.state ? ", " + l.state : ""}` : "—"}</td>
                      <td className="py-3 pr-4 text-[var(--brand-cyan,#FF85AC)]">{l.promotion_title || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {leads.length < leadsTotal && (
                <button
                  onClick={loadMoreLeads}
                  disabled={loadingMoreLeads}
                  data-testid="load-more-leads-btn"
                  className="w-full mt-3 py-2.5 rounded-full border border-white/15 text-sm text-neutral-400 hover:border-[var(--brand-cyan,#FF85AC)]/40 hover:text-[var(--brand-cyan,#FF85AC)] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {loadingMoreLeads && <Loader2 className="w-4 h-4 animate-spin" />}
                  Cargar más ({leadsTotal - leads.length} restantes)
                </button>
              )}
            </div>
          )}
        </article>

        {/* Viajeros */}
        <article className="glass rounded-3xl p-8 border border-white/10" data-testid="travelers-list">
          <div className="flex items-center gap-3 mb-6">
            <Users2 className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Viajeros</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              {bookings.length} total
            </span>
          </div>

          {bookings.length === 0 ? (
            <p className="text-sm text-neutral-500">Aún no hay viajeros registrados.</p>
          ) : (
            <>
              <input
                value={travelerSearch}
                onChange={(e) => setTravelerSearch(e.target.value)}
                placeholder="Buscar por nombre, Booking ID o REG…"
                data-testid="traveler-search-input"
                className="w-full mb-4 bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
              />
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredBookings.length === 0 && (
                  <p className="text-sm text-neutral-500">Sin resultados para "{travelerSearch}".</p>
                )}
                {filteredBookings.map((b) => (
                  <div
                    key={b.booking_id}
                    className="flex flex-wrap items-center justify-between gap-3 p-3.5 rounded-2xl bg-black/40 border border-white/10"
                    data-testid={`traveler-row-${b.booking_id}`}
                  >
                    <div className="min-w-0 [overflow-wrap:anywhere]">
                      <p className="text-sm text-white font-medium">{b.traveler_name}</p>
                      <p className="text-xs text-neutral-500 font-mono">
                        {b.booking_id}{b.file_id ? ` · ${b.file_id}` : ""}
                      </p>
                      {(b.email || b.phone) && (
                        <p className="text-xs text-neutral-600 mt-0.5">{[b.email, b.phone].filter(Boolean).join(" · ")}</p>
                      )}
                    </div>
                    <button
                      onClick={() => resetTravelerPassword(b.booking_id)}
                      disabled={resettingTravelerId === b.booking_id}
                      data-testid={`reset-traveler-password-${b.booking_id}`}
                      className="text-xs uppercase tracking-[0.2em] text-[var(--brand-cyan,#FF85AC)] hover:bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/30 rounded-full px-4 py-2 disabled:opacity-40"
                    >
                      {resettingTravelerId === b.booking_id ? "Regenerando…" : "Resetear contraseña"}
                    </button>
                  </div>
                ))}
              </div>
              {resetTravelerCredential && (
                <GeneratedCredential
                  testId="reset-traveler-credential"
                  heading={`Nueva contraseña para ${resetTravelerCredential.traveler_name}`}
                  loginUrl={`${window.location.origin}/a/${user?.agency_slug || "default"}/login`}
                  bookingId={resetTravelerCredential.booking_id}
                  secondaryLabel="Apellido"
                  secondaryValue={resetTravelerCredential.last_name}
                  password={resetTravelerCredential.password}
                  phone={resetTravelerCredential.phone}
                  email={resetTravelerCredential.email}
                  greetingName={(resetTravelerCredential.traveler_name || "").split(" ")[0]}
                  productName={settings.agency_name}
                />
              )}
            </>
          )}
        </article>

        {/* Chats */}
        <article ref={chatCardRef} className="glass rounded-3xl p-8 border border-white/10 scroll-mt-6">
          <div className="flex items-center gap-3 mb-6">
            <MessageCircle className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Chats con Viajeros</h2>
            {unreadIds.length > 0 && (
              <span
                data-testid="unread-chats-badge"
                className="ml-auto text-[10px] uppercase tracking-[0.25em] text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-full px-3 py-1"
              >
                {unreadIds.length} sin leer
              </span>
            )}
          </div>

          {bookings.length === 0 ? (
            <p className="text-sm text-neutral-500">Aún no hay viajeros registrados.</p>
          ) : (
            <>
              <div className="mb-5">
                <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-2">Viajero</label>
                <select
                  value={chatBookingId}
                  onChange={(e) => setChatBookingId(e.target.value)}
                  data-testid="chat-booking-select"
                  className="w-full sm:w-80 bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-3 py-2.5 text-white"
                >
                  {bookings.map((b) => (
                    <option key={b.booking_id} value={b.booking_id}>
                      {unreadIds.includes(b.booking_id) ? "🔴 " : ""}{b.traveler_name} · {b.booking_id}
                    </option>
                  ))}
                </select>
              </div>

              <div
                ref={chatScrollRef}
                className="h-80 overflow-y-auto overscroll-contain rounded-2xl bg-black/40 border border-white/10 p-4 mb-4 space-y-3"
                data-testid="admin-chat-window"
              >
                {chatMsgs.length === 0 && (
                  <p className="text-center text-neutral-500 text-sm py-10">Sin mensajes con este viajero todavía.</p>
                )}
                {chatMsgs.map((m) => {
                  const mine = m.sender_role === "staff";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                        mine ? "bg-[var(--brand-cyan,#FF85AC)] text-black" : "bg-[#0A0A0A] border border-white/10 text-white"
                      }`}>
                        <p className="text-[10px] uppercase tracking-[0.2em] opacity-60 mb-1">{m.sender}</p>
                        {m.type === "text" && <p className="text-sm leading-relaxed [overflow-wrap:anywhere]">{m.content}</p>}
                        {m.type === "image" && <img src={m.content} alt="" className="rounded-xl max-w-full" />}
                        {m.type === "audio" && <audio src={m.content} controls className="w-64 max-w-full" />}
                        {m.type === "video" && <video src={m.content} controls className="rounded-xl max-w-full max-h-72" />}
                        {m.caption && m.type !== "text" && <p className="text-xs opacity-70 mt-1">{m.caption}</p>}
                        {mine && (
                          <div className="flex justify-end mt-1">
                            {m.read
                              ? <CheckCheck className="w-3.5 h-3.5 opacity-70" />
                              : <Check className="w-3.5 h-3.5 opacity-50" />}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={sendChatReply} className="flex items-center gap-2">
                <input
                  value={chatText}
                  onChange={(e) => setChatText(e.target.value)}
                  placeholder="Responder como soporte…"
                  data-testid="admin-chat-input"
                  className="flex-1 bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-full px-4 py-2.5 text-sm text-white"
                />
                <button
                  type="submit"
                  data-testid="admin-chat-send-btn"
                  className="bg-[var(--brand-cyan,#FF85AC)] text-black p-2.5 rounded-full hover:bg-[#FF9DC0]"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </article>

        {/* Feedback */}
        <article className="glass rounded-3xl p-8 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <Star className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Feedback de Viajeros</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-[var(--brand-cyan,#FF85AC)]">
              {feedbacks.length} de {feedbackTotal} reseñas
            </span>
          </div>
          {feedbacks.length === 0 ? (
            <p className="text-sm text-neutral-500">Aún no hay feedback recibido.</p>
          ) : (
            <div className="space-y-3" data-testid="feedback-list">
              {feedbacks.map((f) => (
                <div key={f.id} className="p-4 rounded-2xl bg-black/40 border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`w-3.5 h-3.5 ${i < f.rating ? "text-[var(--brand-cyan,#FF85AC)] fill-[var(--brand-cyan,#FF85AC)]" : "text-neutral-700"}`}
                        />
                      ))}
                    </div>
                    <span className="text-[10px] text-neutral-500">{f.booking_id} · {f.created_at}</span>
                  </div>
                  {f.service_title && (
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--brand-cyan,#FF85AC)] mb-1.5">{f.service_title}</p>
                  )}
                  {f.comment && <p className="text-sm text-neutral-300 [overflow-wrap:anywhere]">{f.comment}</p>}
                  {f.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {f.categories.map((c) => (
                        <span key={c} className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 border border-white/10 rounded-full px-2 py-0.5">
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => replyToFeedback(f.booking_id)}
                    data-testid={`reply-feedback-${f.id}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[var(--brand-cyan,#FF85AC)] hover:underline"
                  >
                    <MessageCircle className="w-3 h-3" /> Responder por chat
                  </button>
                </div>
              ))}
              {feedbacks.length < feedbackTotal && (
                <button
                  onClick={loadMoreFeedback}
                  disabled={loadingMoreFeedback}
                  data-testid="load-more-feedback-btn"
                  className="w-full py-2.5 rounded-full border border-white/15 text-sm text-neutral-400 hover:border-[var(--brand-cyan,#FF85AC)]/40 hover:text-[var(--brand-cyan,#FF85AC)] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {loadingMoreFeedback && <Loader2 className="w-4 h-4 animate-spin" />}
                  Cargar más ({feedbackTotal - feedbacks.length} restantes)
                </button>
              )}
            </div>
          )}
        </article>

        {/* Promotions */}
        <article className="glass rounded-3xl p-8 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <Megaphone className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Promociones</h2>
            <span className="ml-auto text-[10px] uppercase tracking-[0.25em] text-[var(--brand-cyan,#FF85AC)]">
              {promotions.length} activas
            </span>
          </div>

          {promoError && <p className="text-sm text-[#FF3B30] mb-4">{promoError}</p>}
          {promoSentMsg && <p className="text-sm text-[var(--brand-cyan,#FF85AC)] mb-4">{promoSentMsg}</p>}

          <div className="space-y-3 mb-6" data-testid="promotions-list">
            {promotions.map((p) => (
              <div key={p.id} className="p-4 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-white">{p.title} <span className="text-neutral-500 text-sm">· {p.destination}</span></p>
                  <p className="text-xs text-neutral-500">Desde ${p.price_from} {p.currency}{p.discount ? ` · ${p.discount}% off` : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => sendPromotionPush(p.id)}
                    disabled={sendingPromoId === p.id}
                    data-testid={`send-promo-push-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] px-3 py-1.5 rounded-full bg-[var(--brand-cyan,#FF85AC)] text-black font-semibold hover:bg-[#FF9DC0] disabled:opacity-50"
                  >
                    {sendingPromoId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Enviar push
                  </button>
                  <button onClick={() => deletePromotion(p.id)} data-testid={`delete-promo-${p.id}`} className="text-neutral-400 hover:text-[#FF3B30] p-1.5">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {promotions.length === 0 && <p className="text-sm text-neutral-500">Aún no has creado ninguna promoción.</p>}
          </div>

          <form onSubmit={createPromotion} className="grid sm:grid-cols-2 gap-4">
            <input value={newPromo.title} onChange={(e) => setNewPromo({ ...newPromo, title: e.target.value })}
              placeholder="Título (ej. Escapada a Cancún)" required data-testid="new-promo-title"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white sm:col-span-2" />
            <input value={newPromo.destination} onChange={(e) => setNewPromo({ ...newPromo, destination: e.target.value })}
              placeholder="Destino" required data-testid="new-promo-destination"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
            <input value={newPromo.subtitle} onChange={(e) => setNewPromo({ ...newPromo, subtitle: e.target.value })}
              placeholder="Subtítulo (opcional, es lo que va en el push)" data-testid="new-promo-subtitle"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
            <input value={newPromo.price_from} onChange={(e) => setNewPromo({ ...newPromo, price_from: e.target.value })}
              type="number" placeholder="Precio desde" required data-testid="new-promo-price"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
            <input value={newPromo.image_url} onChange={(e) => setNewPromo({ ...newPromo, image_url: e.target.value })}
              placeholder="URL de imagen (opcional)" data-testid="new-promo-image"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white sm:col-span-2" />
            <div className="sm:col-span-2">
              <button type="submit" disabled={creatingPromo} data-testid="create-promo-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold bg-[var(--brand-cyan,#FF85AC)] text-black hover:bg-[#FF9DC0] disabled:opacity-50">
                {creatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Crear promoción
              </button>
            </div>
          </form>
        </article>

        {/* Admin management */}
        <article className="glass rounded-3xl p-8 border border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <UserCog className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Administradores</h2>
          </div>

          <div className="space-y-3 mb-6" data-testid="admins-list">
            {admins.map((a) => (
              <div key={a.booking_id} className="flex items-center justify-between gap-4 p-4 rounded-2xl bg-black/40 border border-white/10">
                <div className="min-w-0 [overflow-wrap:anywhere]">
                  <p className="text-white">
                    {a.traveler_name} <span className="text-neutral-500 text-sm">· {a.booking_id}</span>
                  </p>
                  <p className="text-xs text-neutral-500">{a.email || "sin correo"}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${
                    a.active
                      ? "text-[var(--brand-cyan,#FF85AC)] border-[var(--brand-cyan,#FF85AC)]/30 bg-[var(--brand-cyan,#FF85AC)]/10"
                      : "text-neutral-500 border-white/10"
                  }`}>
                    {a.active ? "Activo" : "Deshabilitado"}
                  </span>
                  <button
                    onClick={() => resetAdminPwd(a)}
                    disabled={resettingAdminId === a.booking_id}
                    data-testid={`reset-password-${a.booking_id}`}
                    title="Resetear contraseña"
                    className="text-neutral-400 hover:text-[var(--brand-cyan,#FF85AC)] p-2 disabled:opacity-50"
                  >
                    {resettingAdminId === a.booking_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => toggleAdmin(a)}
                    data-testid={`toggle-admin-${a.booking_id}`}
                    title={a.active ? "Deshabilitar" : "Habilitar"}
                    className="text-neutral-400 hover:text-[var(--brand-cyan,#FF85AC)] p-2"
                  >
                    <Power className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {adminError && <p className="text-sm text-[#FF3B30] mb-4">{adminError}</p>}

          <form onSubmit={addAdmin} className="grid sm:grid-cols-2 gap-4">
            <input
              value={newAdmin.booking_id}
              onChange={(e) => setNewAdmin({ ...newAdmin, booking_id: e.target.value })}
              placeholder="ID de operador (ej. ADMIN-002)"
              data-testid="new-admin-id"
              required
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <input
              value={newAdmin.last_name}
              onChange={(e) => setNewAdmin({ ...newAdmin, last_name: e.target.value })}
              placeholder="Apellido (referencia)"
              data-testid="new-admin-key"
              required
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <input
              value={newAdmin.traveler_name}
              onChange={(e) => setNewAdmin({ ...newAdmin, traveler_name: e.target.value })}
              placeholder="Nombre a mostrar"
              data-testid="new-admin-name"
              required
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <input
              value={newAdmin.email}
              onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
              placeholder="Correo (opcional)"
              type="email"
              data-testid="new-admin-email"
              className="bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white"
            />
            <button
              type="submit"
              disabled={addingAdmin}
              data-testid="add-admin-btn"
              className="sm:col-span-2 bg-[var(--brand-cyan,#FF85AC)] text-black px-6 py-2.5 rounded-full font-semibold hover:bg-[#FF9DC0] disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {addingAdmin ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Agregar administrador
            </button>
          </form>

          {newAdminCredential && (
            <GeneratedCredential
              testId="new-admin-credentials"
              heading={
                newAdminCredential.isReset
                  ? `Contraseña reseteada para ${newAdminCredential.traveler_name}`
                  : `Acceso creado para ${newAdminCredential.traveler_name}`
              }
              loginUrl={`${window.location.origin}/admin/login`}
              bookingId={newAdminCredential.booking_id}
              password={newAdminCredential.password}
              email={newAdminCredential.email}
              greetingName={newAdminCredential.traveler_name.split(" ")[0]}
              productName={settings.agency_name}
            />
          )}
        </article>
      </section>
    </Layout>
  );
}
